import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { InspectionQuestion, InspectionWorkflow, WorkflowStep, IterateStep } from "src/components/pages/configurationDrivenInspections/types/inspectionWorkflow";
import { apiRequest } from "src/utils/apiRequest";

export type WorkflowState = {
    workflows: InspectionWorkflow[];
    activeWorkflow: InspectionWorkflow | null;
    questions: InspectionQuestion[];
    showWorkflowHeader: boolean;
    currentStep: WorkflowStep | IterateStep | null;
    currentIterateStep: WorkflowStep | null;
    shownHumanText: string;
    inspectionId: string;
    currentIterationIndex: number; // 0-based index of current iteration
    totalIterations: number; // Total number of iterations (drum count)
};

const initialState: WorkflowState = {
    workflows: [],
    activeWorkflow: null,
    questions: [],
    showWorkflowHeader: true,
    currentStep: null,
    currentIterateStep: null,
    shownHumanText: "",
    inspectionId: '', // Will be set when workflow inspection is created
    currentIterationIndex: 0,
    totalIterations: 0
}

export const getInspectionWorkflow = createAsyncThunk(
    "inspectionWorkflow/getInspectionWorkflow",
    async (_, { rejectWithValue }) => {
        try {
            const data = await apiRequest({url: "/inspections/workflow-config", method: "GET" });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to fetch inspection workflow");
        }
    }
);

export const createWorkflowInspection = createAsyncThunk(
    "workflow/createWorkflowInspection",
    async ({ templateId, templateName }: { templateId: string; templateName: string }, { rejectWithValue }) => {
        try {
            const data = await apiRequest({
                url: "/inspections",
                method: "POST",
                data: {
                    templateId,
                    templateName,
                    siteCode: "ABE2",
                },
            });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to create workflow inspection");
        }
    }
);

/** Inspection Template Slice */
const { reducer, actions } = createSlice({
  name: "workflow",
  initialState,
  reducers: {
    setShowWorkflowHeader: (state, { payload }) => {
      state.showWorkflowHeader = payload;
    },
    setShownHumanText: (state, { payload }) => {
      state.shownHumanText = payload;
    },
    setCurrentStep: (state, { payload }) => {
      state.currentStep = payload;
    },
    setCurrentIterateStep: (state, { payload }) => {
      state.currentIterateStep = payload;
    },
    setIterationProgress: (state, { payload }: { payload: { currentIndex: number; total: number } }) => {
      state.currentIterationIndex = payload.currentIndex;
      state.totalIterations = payload.total;
    },
    setActiveWorkflow: (state, { payload }: { payload: InspectionWorkflow }) => {
      state.activeWorkflow = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getInspectionWorkflow.fulfilled,
      (state, { payload }) => {
        state.workflows = payload.workflows ?? [];
        state.questions = payload.questions;
      }
    );
    
    builder.addCase(
      createWorkflowInspection.fulfilled,
      (state, { payload }) => {
        // Set inspectionId from the created inspection response
        state.inspectionId = payload.inspectionId;
      }
    );
  },
});

export const { setShowWorkflowHeader, setShownHumanText, setCurrentStep, setCurrentIterateStep, setIterationProgress, setActiveWorkflow } = actions;

export default reducer;
